import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Package,
  Warning,
  Image as ImageIcon,
  TextAa,
  ArrowsClockwise,
  ChartLineUp,
  CheckCircle,
  XCircle,
  FilePdf,
  Robot,
  LinkBreak,
  Clock,
  Lightning,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, PieChart, Pie, Legend, CartesianGrid
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const SEO_COLORS = { high: '#16a34a', medium: '#f59e0b', low: '#dc2626', none: '#94a3b8' };

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const navigate = useNavigate();

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, syncRes, actRes] = await Promise.all([
        axios.get(`${API}/api/products/stats`, { headers: getHeaders() }),
        axios.get(`${API}/api/sync/status`, { headers: getHeaders() }),
        axios.get(`${API}/api/reports/activity?days=30`, { headers: getHeaders() }),
      ]);
      setStats(statsRes.data);
      setSyncStatus(syncRes.data);
      setActivity(actRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data', err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startSync = async () => {
    try {
      await axios.post(`${API}/api/sync/products`, {}, { headers: getHeaders() });
      setSyncStatus({ ...syncStatus, status: 'syncing', in_progress: true });
      const interval = setInterval(async () => {
        const res = await axios.get(`${API}/api/sync/status`, { headers: getHeaders() });
        setSyncStatus(res.data);
        if (!res.data.in_progress) {
          clearInterval(interval);
          fetchData();
        }
      }, 3000);
    } catch (err) {
      console.error('Sync failed', err);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await axios.get(`${API}/api/reports/pdf`, {
        headers: getHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `catalogguard-rapport-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF lastet ned');
    } catch {
      toast.error('Kunne ikke laste ned PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-muted animate-pulse rounded-md" />)}
        </div>
      </div>
    );
  }

  const issues = stats?.issues || {};
  const totalIssues = Object.values(issues).reduce((a, b) => a + b, 0);
  const seo = activity?.seo_distribution || { low: 0, medium: 0, high: 0, none: 0 };
  const pieData = [
    { name: 'Høy (80+)', value: seo.high || 0, color: SEO_COLORS.high },
    { name: 'Middels (50-79)', value: seo.medium || 0, color: SEO_COLORS.medium },
    { name: 'Lav (<50)', value: seo.low || 0, color: SEO_COLORS.low },
    { name: 'Mangler', value: seo.none || 0, color: SEO_COLORS.none },
  ].filter((d) => d.value > 0);

  const actionIcon = (type, action) => {
    if (type === 'link') return <LinkBreak size={14} className="text-warning" />;
    if (action === 'auto_applied') return <Lightning size={14} className="text-success" />;
    if (action === 'approved_and_pushed') return <CheckCircle size={14} className="text-primary" />;
    return <Clock size={14} className="text-muted-foreground" />;
  };

  const actionLabel = (type, action) => {
    if (type === 'link') return action === 'link_removed' ? 'Lenke fjernet' : 'Lenke erstattet';
    if (action === 'auto_applied') return 'Auto-oppdatert';
    if (action === 'approved_and_pushed') return 'Godkjent & pushet';
    return action || 'Aktivitet';
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold">Dashboard</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Oversikt over butikkens helse og aktivitet</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadPdf}
            disabled={pdfLoading}
            data-testid="download-pdf-btn"
            className="gap-2"
          >
            <FilePdf size={16} weight="bold" />
            <span className="hidden sm:inline">{pdfLoading ? 'Genererer...' : 'Last ned PDF-rapport'}</span>
            <span className="sm:hidden">{pdfLoading ? 'PDF...' : 'PDF'}</span>
          </Button>
          <Button
            size="sm"
            onClick={startSync}
            disabled={syncStatus?.in_progress}
            data-testid="sync-button"
            className="gap-2"
          >
            <ArrowsClockwise size={16} weight="bold" className={syncStatus?.in_progress ? 'animate-spin' : ''} />
            {syncStatus?.in_progress
              ? `Synkroniserer... ${syncStatus?.total_synced ? `(${syncStatus.total_synced})` : ''}`
              : 'Sync nå'}
          </Button>
          {syncStatus?.in_progress && (
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                try {
                  await axios.post(`${API}/api/sync/reset`, {}, { headers: getHeaders() });
                  toast.success('Synkronisering nullstilt');
                  fetchData();
                } catch {
                  toast.error('Kunne ikke nullstille');
                }
              }}
              data-testid="sync-reset-btn"
              className="text-xs text-muted-foreground hover:text-destructive"
            >
              Avbryt
            </Button>
          )}
        </div>
      </div>

      {/* Sync Status Bar */}
      {syncStatus?.last_sync && (() => {
        const lastSync = new Date(syncStatus.last_sync);
        const ageHours = (Date.now() - lastSync.getTime()) / (1000 * 60 * 60);
        const isStale = ageHours > 24;
        const isFresh = ageHours < 6;
        const ageLabel = ageHours < 1
          ? `${Math.round(ageHours * 60)} min siden`
          : ageHours < 24
            ? `${Math.round(ageHours)} timer siden`
            : `${Math.round(ageHours / 24)} dager siden`;
        return (
          <div
            className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md ${
              isStale ? 'bg-amber-50 text-amber-800 border border-amber-200'
                : isFresh ? 'text-muted-foreground'
                  : 'text-muted-foreground'
            }`}
            data-testid="sync-status-info"
          >
            {isStale ? <span>⚠️</span> : <CheckCircle size={14} className="text-success" />}
            <span>
              {isStale ? <strong>Data er over 24 timer gammel.</strong> : 'Sist synkronisert'}
              {' '}for {ageLabel} &mdash; {syncStatus.total_synced} produkter.
              {' '}<span className="text-muted-foreground/80">Auto-sync hver natt kl. 03:00.</span>
              {isStale && <> Trykk <strong>Sync nå</strong> for ferske data.</>}
            </span>
          </div>
        );
      })()}

      {/* Health Score + Totals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-border lg:col-span-1" data-testid="health-score-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Butikkhelse</p>
                <p className="font-heading text-4xl font-extrabold mt-1">{stats?.health_score || 0}%</p>
              </div>
              <ChartLineUp size={40} weight="regular" className="text-primary opacity-50" />
            </div>
            <Progress value={stats?.health_score || 0} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">{totalIssues} problemer av {stats?.total || 0} produkter</p>

            {/* Weighted breakdown */}
            {stats?.health_breakdown && (
              <div className="mt-4 pt-4 border-t border-border/50 space-y-2" data-testid="health-breakdown">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Vektet sammensetning</p>
                {Object.entries({
                  products: 'Produkter',
                  categories: 'Kategorier',
                  terms: 'Attributter',
                  images: 'Bilde-alt',
                }).map(([key, label]) => {
                  const b = stats.health_breakdown[key];
                  if (!b) return null;
                  return (
                    <div key={key} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xs">{label}</span>
                        <span className="text-[10px] text-muted-foreground">({b.weight}% vekt · {b.count})</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary" style={{ width: `${b.score}%` }} />
                        </div>
                        <span className="text-xs font-mono w-8 text-right">{b.score}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border lg:col-span-2" data-testid="activity-totals">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Aktivitet siste 30 dager</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-muted/50 rounded-md">
                <p className="font-heading text-2xl font-bold text-success">{activity?.totals?.autoguard_actions || 0}</p>
                <p className="text-xs text-muted-foreground">AutoGuard-handlinger</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-md">
                <p className="font-heading text-2xl font-bold text-primary">{activity?.totals?.approvals || 0}</p>
                <p className="text-xs text-muted-foreground">Godkjente pushes</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-md">
                <p className="font-heading text-2xl font-bold text-warning">{activity?.totals?.pending_approvals || 0}</p>
                <p className="text-xs text-muted-foreground">Venter godkjenning</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-md">
                <p className="font-heading text-2xl font-bold text-destructive">{activity?.totals?.links_broken_now || 0}</p>
                <p className="text-xs text-muted-foreground">Ødelagte lenker nå</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts: Activity trend + SEO distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-border lg:col-span-2" data-testid="activity-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Aktivitet over tid</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={activity?.daily || []} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => new Date(d).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short' })}
                  stroke="#94a3b8"
                  fontSize={11}
                />
                <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 6, color: '#fff' }}
                  labelFormatter={(d) => new Date(d).toLocaleDateString('nb-NO')}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="seo_applied" name="SEO auto" stroke="#16a34a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="approvals" name="Godkjent" stroke="#2563eb" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="links_fixed" name="Lenker fikset" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border" data-testid="seo-distribution-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">SEO-fordeling</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={pieData}
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={2}
                    dataKey="value"
                    label={(e) => `${Math.round((e.percent || 0) * 100)}%`}
                    labelLine={false}
                  >
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">Ingen data ennå</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Card className="border border-border cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/products')} data-testid="stat-total-products">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Package size={20} className="text-primary" />
              <Badge variant="secondary">{stats?.published || 0} aktive</Badge>
            </div>
            <p className="font-heading text-2xl font-bold mt-2">{stats?.total || 0}</p>
            <p className="text-xs text-muted-foreground">Totalt produkter</p>
          </CardContent>
        </Card>

        <Card className="border border-border cursor-pointer hover:border-destructive/30 transition-colors" onClick={() => navigate('/products?issue=missing_description')} data-testid="stat-missing-desc">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <TextAa size={20} className="text-destructive" />
              <XCircle size={16} className="text-destructive" />
            </div>
            <p className="font-heading text-2xl font-bold mt-2">{issues.missing_description || 0}</p>
            <p className="text-xs text-muted-foreground">Mangler beskrivelse</p>
          </CardContent>
        </Card>

        <Card className="border border-border cursor-pointer hover:border-warning/30 transition-colors" onClick={() => navigate('/products?issue=missing_images')} data-testid="stat-missing-images">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <ImageIcon size={20} className="text-warning" />
              <Warning size={16} className="text-warning" />
            </div>
            <p className="font-heading text-2xl font-bold mt-2">{issues.missing_images || 0}</p>
            <p className="text-xs text-muted-foreground">Mangler bilder</p>
          </CardContent>
        </Card>

        <Card className="border border-border cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate('/products?issue=low_seo')} data-testid="stat-low-seo">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <ChartLineUp size={20} className="text-primary" />
              <Badge variant="outline">&lt;50</Badge>
            </div>
            <p className="font-heading text-2xl font-bold mt-2">{issues.low_seo || 0}</p>
            <p className="text-xs text-muted-foreground">Lav SEO-score</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent activity feed */}
      <Card className="border border-border" data-testid="recent-activity-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Siste aktivitet</CardTitle>
        </CardHeader>
        <CardContent>
          {activity?.recent_activity?.length > 0 ? (
            <div className="divide-y divide-border">
              {activity.recent_activity.slice(0, 15).map((a, i) => (
                <div
                  key={`${a.product_id}-${a.timestamp}-${i}`}
                  className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-muted/20 -mx-2 px-2 rounded"
                  onClick={() => a.product_id && navigate(`/products/${a.product_id}`)}
                  data-testid={`activity-row-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {actionIcon(a.type, a.action)}
                    <div className="min-w-0">
                      <p className="text-sm truncate max-w-[420px]">{a.product_name || `Produkt #${a.product_id}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {actionLabel(a.type, a.action)}
                        {a.changes && a.changes.length > 0 && ` — ${a.changes.filter((c) => !['modified_locally', 'local_modified_at', 'autoguard_auto', 'seo_score'].includes(c)).slice(0, 3).join(', ')}`}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {a.timestamp ? new Date(a.timestamp).toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">
              <Robot size={28} className="mx-auto mb-2 opacity-40" />
              Ingen aktivitet ennå. Start AutoGuard for å begynne.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
